import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import ChipAIGenerated from "../Chip/ChipAIGenerated";
import { useAIGeneratedLeadFormDraft } from "../../hooks/useAIGeneratedSection";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import ContainerModalActions from "../Containers/ContainerModalActions";
import ModalBasicLayout, {
  ModalActionsPortal,
} from "../Modal/ModalBasicLayout";
import TextField from "../Form/TextField";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../Form/Select";
import Checkbox from "../Form/Checkbox";
import {
  AddLinkButton,
  CustomQuestionFields,
  FieldWithExternalLabel,
  FormSection,
  SectionTitleWithCounter,
} from "../LeadForm/LeadFormFields";
import { MULTILINE_RESIZE_SX } from "../LeadForm/leadFormStyles";
import { SimpleAlert } from "../Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import { startCreateLeadForm } from "../../actions/adsCampaigns";
import RecommendedLeadFormNotice from "../LeadForm/RecommendedLeadFormNotice";
import {
  useApplyRecommendedLeadForm,
  useRecommendedLeadForm,
} from "../../hooks/useRecommendedLeadForm";
import { LANGUAGES_LINKEDIN_OPTIONS } from "../../utils/catalogs/languageOptions";
import {
  LANGUAGE_CODE_BY_URN,
  resolveLinkedInLanguageCode,
} from "../../utils/linkedInLeadFormLanguage";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../utils/regex";
import { LINKEDIN } from "../../utils/constants";
import {
  DEFAULT_LINKEDIN_LEAD_FORM_CALL_TO_ACTION,
  DEFAULT_LINKEDIN_LEAD_FORM_PROFILE_FIELDS,
  LINKEDIN_LEAD_FORM_CALL_TO_ACTION_OPTIONS,
  LINKEDIN_LEAD_FORM_FIELD_LIMITS,
  MAX_LINKEDIN_LEAD_FORM_QUESTIONS,
} from "../../utils/linkedInLeadForm";
import { LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS } from "../../utils/linkedInLeadFormProfileFields";
import {
  BUTTON_ADD,
  BUTTON_CREATE,
  BUTTON_DUPLICATE,
  BUTTON_GO_BACK,
  ERROR_OCCURRED,
  FIELD_MAX_LENGTH,
  FIELD_REQUIRED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  LANGUAGE_PLACEHOLDER,
  MODAL_CREATE_LEAD_FORM_CTA,
  MODAL_CREATE_LEAD_FORM_CUSTOM_QUESTIONS,
  MODAL_CREATE_LEAD_FORM_DESCRIPTION,
  MODAL_CREATE_LEAD_FORM_DETAILS,
  MODAL_CREATE_LEAD_FORM_HEADLINE,
  MODAL_CREATE_LEAD_FORM_LANDING_PAGE_URL,
  MODAL_CREATE_LEAD_FORM_MAX_CUSTOM_QUESTIONS,
  MODAL_CREATE_LEAD_FORM_NAME,
  MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_TEXT,
  MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_URL,
  MODAL_CREATE_LEAD_FORM_PROFILE_FIELDS,
  MODAL_CREATE_LEAD_FORM_SECTION_CONFIRMATION,
  MODAL_CREATE_LEAD_FORM_SECTION_CONTACT,
  MODAL_CREATE_LEAD_FORM_SECTION_FORM,
  MODAL_CREATE_LEAD_FORM_SECTION_PRIVACY,
  MODAL_CREATE_LEAD_FORM_THANK_YOU_MESSAGE,
  MODAL_CREATE_LEAD_FORM_TITLE,
  MODAL_LEAD_FORMS_COPY_NAME_PREFIX,
  MODAL_VIEW_LEAD_FORM_DESCRIPTION,
  MODAL_VIEW_LEAD_FORM_TITLE,
} from "../../i18n/keysTranslations";

const {
  name: NAME_MAX_LENGTH,
  headline: HEADLINE_MAX_LENGTH,
  description: DESCRIPTION_MAX_LENGTH,
  url: URL_MAX_LENGTH,
  legalDisclaimer: LEGAL_DISCLAIMER_MAX_LENGTH,
  thankYouMessage: THANK_YOU_MESSAGE_MAX_LENGTH,
} = LINKEDIN_LEAD_FORM_FIELD_LIMITS;

const LEAD_FORM_LANGUAGE_OPTIONS = LANGUAGES_LINKEDIN_OPTIONS.map((option) => ({
  label: option.label,
  value: LANGUAGE_CODE_BY_URN[option.value] || "en",
}));

const SUPPORTED_LANGUAGE_CODES = new Set(
  LEAD_FORM_LANGUAGE_OPTIONS.map((option) => option.value)
);

const resolveDefaultLanguage = (campaignLanguage) => {
  const normalized = resolveLinkedInLanguageCode(campaignLanguage);
  if (SUPPORTED_LANGUAGE_CODES.has(normalized)) {
    return normalized;
  }
  return "en";
};

const ModalCreateLeadForm = ({
  modalOpen,
  onCloseModal = () => {},
  onCallbackSave = () => {},
  integrationID,
  adAccountID,
  campaignLanguage = "en",
  initialValues = null,
  readOnly = false,
  leadFormRecommendation = null,
  aiGeneratedDraft = false,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const isReadOnly = readOnly && !isDuplicating;
  const defaultLanguage = resolveDefaultLanguage(
    initialValues?.language || campaignLanguage
  );

  const {
    control,
    getValues,
    formState,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
      language: defaultLanguage,
      headline: "",
      description: "",
      profileFields: DEFAULT_LINKEDIN_LEAD_FORM_PROFILE_FIELDS,
      customQuestions: [],
      privacyPolicyUrl: "",
      legalDisclaimer: "",
      thankYouMessage: "",
      callToActionLabel: DEFAULT_LINKEDIN_LEAD_FORM_CALL_TO_ACTION,
      landingPageUrl: "",
      ...(initialValues || {}),
    },
  });

  useFocusErrorForm(formState);
  const showAIGeneratedDraft = useAIGeneratedLeadFormDraft({
    enabled: aiGeneratedDraft,
    control,
    getValues,
  });
  const { errors } = formState;
  const recommendation = useRecommendedLeadForm({
    platform: LINKEDIN,
    adID: leadFormRecommendation?.adID,
    adGroupID: leadFormRecommendation?.adGroupID,
    enabled: Boolean(leadFormRecommendation) && !initialValues && !isReadOnly,
    campaignID: leadFormRecommendation?.campaignID,
    advertiserID: leadFormRecommendation?.advertiserID,
    language: leadFormRecommendation?.language ?? campaignLanguage,
    getCampaignValues: leadFormRecommendation?.getCampaignValues,
    setCampaignValue: leadFormRecommendation?.setCampaignValue,
  });
  useApplyRecommendedLeadForm({ recommendation, reset, getValues });
  const isGeneratingRecommendation = recommendation.isGenerating;
  const isEditorLocked =
    isSubmitting || isReadOnly || isGeneratingRecommendation;
  const selectedProfileFields = watch("profileFields") || [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: "customQuestions",
  });

  const profileFieldsCount = Array.isArray(selectedProfileFields)
    ? selectedProfileFields.length
    : 0;
  const customQuestionsCount = fields.length;
  const maxProfileFields = Math.max(
    0,
    MAX_LINKEDIN_LEAD_FORM_QUESTIONS - customQuestionsCount
  );
  const maxCustomQuestions = Math.max(
    0,
    MAX_LINKEDIN_LEAD_FORM_QUESTIONS - profileFieldsCount
  );
  const isContactQuestionsLimitReached =
    profileFieldsCount + customQuestionsCount >=
    MAX_LINKEDIN_LEAD_FORM_QUESTIONS;

  const urlRules = useMemo(
    () => ({
      required: {
        value: true,
        message: t(FIELD_REQUIRED),
      },
      maxLength: {
        value: URL_MAX_LENGTH,
        message: t(FIELD_MAX_LENGTH, { value: URL_MAX_LENGTH }),
      },
      pattern: {
        value: REGEX_URL_WITH_PROTOCOL_REQUIRED,
        message: t(FIELD_URL_VALID_WITH_PROTOCOL),
      },
    }),
    [t]
  );

  const onAddCustomQuestion = () => {
    if (isReadOnly) return;
    if (customQuestionsCount >= maxCustomQuestions) return;
    append({
      question: "",
      type: "TEXT",
      options: ["", ""],
    });
  };

  const onSubmit = async () => {
    if (isEditorLocked) return;
    setIsSubmitting(true);
    try {
      const formValues = getValues();
      const response = await dispatch(
        startCreateLeadForm({
          integrationID,
          adAccountID,
          platform: LINKEDIN,
          name: formValues.name,
          language: formValues.language,
          headline: formValues.headline,
          description: formValues.description,
          profileFields: formValues.profileFields,
          customQuestions: formValues.customQuestions,
          privacyPolicyUrl: formValues.privacyPolicyUrl,
          legalDisclaimer: formValues.legalDisclaimer,
          thankYouMessage: formValues.thankYouMessage,
          callToActionLabel: formValues.callToActionLabel,
          landingPageUrl: formValues.landingPageUrl,
        })
      );

      if (!response?.ok || !response?.data?.id) {
        SimpleAlert({
          icon: "error",
          title: t(ERROR_OCCURRED),
          text: response?.message || t(ERROR_OCCURRED),
        });
        return;
      }

      onCallbackSave({
        id: response.data.id,
        name: response.data.name,
        language: formValues.language,
      });
      onCloseModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={isSubmitting ? () => {} : onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(
        isReadOnly ? MODAL_VIEW_LEAD_FORM_TITLE : MODAL_CREATE_LEAD_FORM_TITLE
      )}
      description={t(
        isReadOnly
          ? MODAL_VIEW_LEAD_FORM_DESCRIPTION
          : MODAL_CREATE_LEAD_FORM_DESCRIPTION
      )}
      showCloseButton={!isSubmitting}
    >
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isEditorLocked) return;
          handleSubmit(onSubmit)();
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <RecommendedLeadFormNotice
          isGenerating={isGeneratingRecommendation}
          notice={recommendation.notice}
          onUseDefaults={recommendation.onUseDefaults}
        />
        {!isGeneratingRecommendation ? (
          <>
            {showAIGeneratedDraft ? <ChipAIGenerated /> : null}
            <Box
              component="fieldset"
              disabled={isEditorLocked}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                m: 0,
                p: 0,
                minWidth: 0,
                border: "none",
              }}
            >
              <FormSection
                title={t(MODAL_CREATE_LEAD_FORM_SECTION_FORM)}
                showDivider={false}
              >
                <FieldWithExternalLabel label={t(MODAL_CREATE_LEAD_FORM_NAME)}>
                  <TextField
                    label=""
                    name="name"
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      maxLength: {
                        value: NAME_MAX_LENGTH,
                        message: t(FIELD_MAX_LENGTH, {
                          value: NAME_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel label={t(LANGUAGE_PLACEHOLDER)}>
                  <Select
                    label=""
                    name="language"
                    control={control}
                    errors={errors}
                    options={LEAD_FORM_LANGUAGE_OPTIONS}
                    doTranslate={true}
                    fullWidth
                    variant="filled"
                    disabled={isReadOnly}
                    MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_HEADLINE)}
                >
                  <TextField
                    label=""
                    name="headline"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={4}
                    showCounterCharacters
                    maxCharacters={HEADLINE_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      maxLength: {
                        value: HEADLINE_MAX_LENGTH,
                        message: t(FIELD_MAX_LENGTH, {
                          value: HEADLINE_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_DETAILS)}
                >
                  <TextField
                    label=""
                    name="description"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={6}
                    showCounterCharacters
                    maxCharacters={DESCRIPTION_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={{
                      maxLength: {
                        value: DESCRIPTION_MAX_LENGTH,
                        message: t(FIELD_MAX_LENGTH, {
                          value: DESCRIPTION_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FieldWithExternalLabel>
              </FormSection>
              <FormSection title={t(MODAL_CREATE_LEAD_FORM_SECTION_CONTACT)}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pl: "2px",
                  }}
                >
                  <SectionTitleWithCounter
                    title={t(MODAL_CREATE_LEAD_FORM_PROFILE_FIELDS)}
                    count={profileFieldsCount}
                    max={maxProfileFields}
                    sx={{ display: "block", pb: "8px" }}
                  />
                  <Controller
                    name="profileFields"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!Array.isArray(value) || value.length === 0) {
                          return t(FIELD_REQUIRED);
                        }
                        if (
                          value.length + customQuestionsCount >
                          MAX_LINKEDIN_LEAD_FORM_QUESTIONS
                        ) {
                          return t(MODAL_CREATE_LEAD_FORM_MAX_CUSTOM_QUESTIONS);
                        }
                        return true;
                      },
                    }}
                    render={({ field: { value = [], onChange } }) => (
                      <Box
                        id="profileFields"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {LINKEDIN_LEAD_FORM_PROFILE_FIELD_OPTIONS.map(
                          (option) => {
                            const isChecked = value.includes(option.value);
                            const isDisabled =
                              isReadOnly ||
                              (!isChecked && value.length >= maxProfileFields);
                            return (
                              <FormControlLabel
                                key={option.value}
                                control={
                                  <Checkbox
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    sx={{ p: 0, mr: 1 }}
                                    onChange={() => {
                                      if (isChecked) {
                                        onChange(
                                          value.filter(
                                            (field) => field !== option.value
                                          )
                                        );
                                        return;
                                      }
                                      if (value.length >= maxProfileFields)
                                        return;
                                      onChange([...value, option.value]);
                                    }}
                                  />
                                }
                                label={t(option.label)}
                                sx={{
                                  ml: 0,
                                  mr: 0,
                                  alignItems: "center",
                                }}
                              />
                            );
                          }
                        )}
                        {errors?.profileFields?.message && (
                          <Typography variant="body2" color="error">
                            {errors.profileFields.message}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    mt: 1.5,
                    pl: "2px",
                  }}
                >
                  <SectionTitleWithCounter
                    title={t(MODAL_CREATE_LEAD_FORM_CUSTOM_QUESTIONS)}
                    count={customQuestionsCount}
                    max={maxCustomQuestions}
                  />
                  {fields.map((item, index) => (
                    <CustomQuestionFields
                      key={item.id}
                      index={index}
                      control={control}
                      errors={errors}
                      onRemove={() => remove(index)}
                      t={t}
                      readOnly={isReadOnly}
                      setValue={setValue}
                      getValues={getValues}
                    />
                  ))}
                  {!isReadOnly && (
                    <AddLinkButton
                      onClick={onAddCustomQuestion}
                      disabled={customQuestionsCount >= maxCustomQuestions}
                    >
                      {t(BUTTON_ADD)}
                    </AddLinkButton>
                  )}
                  {isContactQuestionsLimitReached && (
                    <Typography variant="body2" color="text.secondary">
                      {t(MODAL_CREATE_LEAD_FORM_MAX_CUSTOM_QUESTIONS)}
                    </Typography>
                  )}
                </Box>
              </FormSection>
              <FormSection title={t(MODAL_CREATE_LEAD_FORM_SECTION_PRIVACY)}>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_URL)}
                >
                  <TextField
                    label=""
                    name="privacyPolicyUrl"
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={urlRules}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_PRIVACY_POLICY_TEXT)}
                >
                  <TextField
                    label=""
                    name="legalDisclaimer"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={8}
                    showCounterCharacters
                    maxCharacters={LEGAL_DISCLAIMER_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={{
                      maxLength: {
                        value: LEGAL_DISCLAIMER_MAX_LENGTH,
                        message: t(FIELD_MAX_LENGTH, {
                          value: LEGAL_DISCLAIMER_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FieldWithExternalLabel>
              </FormSection>
              <FormSection
                title={t(MODAL_CREATE_LEAD_FORM_SECTION_CONFIRMATION)}
              >
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_THANK_YOU_MESSAGE)}
                >
                  <TextField
                    label=""
                    name="thankYouMessage"
                    variant="filled"
                    multiline
                    minRows={2}
                    maxRows={3}
                    showCounterCharacters
                    maxCharacters={THANK_YOU_MESSAGE_MAX_LENGTH}
                    fullWidth
                    control={control}
                    errors={errors}
                    sx={MULTILINE_RESIZE_SX}
                    rules={{
                      required: {
                        value: true,
                        message: t(FIELD_REQUIRED),
                      },
                      maxLength: {
                        value: THANK_YOU_MESSAGE_MAX_LENGTH,
                        message: t(FIELD_MAX_LENGTH, {
                          value: THANK_YOU_MESSAGE_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel
                  label={t(MODAL_CREATE_LEAD_FORM_LANDING_PAGE_URL)}
                >
                  <TextField
                    label=""
                    name="landingPageUrl"
                    variant="filled"
                    fullWidth
                    control={control}
                    errors={errors}
                    rules={urlRules}
                  />
                </FieldWithExternalLabel>
                <FieldWithExternalLabel label={t(MODAL_CREATE_LEAD_FORM_CTA)}>
                  <Select
                    label=""
                    name="callToActionLabel"
                    control={control}
                    errors={errors}
                    options={LINKEDIN_LEAD_FORM_CALL_TO_ACTION_OPTIONS}
                    doTranslate={true}
                    fullWidth
                    variant="filled"
                    disabled={isReadOnly}
                    MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
                  />
                </FieldWithExternalLabel>
              </FormSection>
            </Box>
          </>
        ) : null}
      </Box>
      {!isGeneratingRecommendation ? (
        <ModalActionsPortal>
          <Box
            sx={{
              px: { xs: 3, sm: 5 },
              pt: 1.5,
              pb: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ContainerModalActions
              hasPadding={false}
              propsSecondaryButton={{
                showButton: true,
                onClick: onCloseModal,
                text: BUTTON_GO_BACK,
                disabled: isSubmitting,
              }}
              propsTertiaryButton={{
                showButton: isReadOnly,
                type: "button",
                text: BUTTON_DUPLICATE,
                disabled: isSubmitting,
                onClick: () => {
                  const prefix = t(MODAL_LEAD_FORMS_COPY_NAME_PREFIX);
                  const currentName = String(getValues("name") || "").trim();
                  const alreadyPrefixed = currentName
                    .toLowerCase()
                    .startsWith(`${prefix.toLowerCase()} `);
                  setValue(
                    "name",
                    !currentName || alreadyPrefixed
                      ? currentName || prefix
                      : `${prefix} ${currentName}`,
                    { shouldDirty: true }
                  );
                  setIsDuplicating(true);
                },
              }}
              propsPrimaryButton={{
                showButton: !isReadOnly,
                text: BUTTON_CREATE,
                type: "button",
                loading: isSubmitting,
                disabled: isEditorLocked,
                onClick: async (event) => {
                  event.preventDefault();
                  if (isEditorLocked) return;
                  await handleSubmit(onSubmit)();
                },
              }}
            />
          </Box>
        </ModalActionsPortal>
      ) : null}
    </ModalBasicLayout>
  );
};

export default ModalCreateLeadForm;
